import { BehaviorSubject, Observable, debounceTime, distinctUntilChanged, firstValueFrom, map, of, tap } from "rxjs";
import { DashboardFilterInterface, DashboardFilter } from "./dashboard_filter";
import { DashboardFilterSelectOption } from "./dashboard_filter_select_option";
import { DashboardFilterUISettingsInterface, DashboardFilterUISettings } from "./dashboard_filter_ui_settings";

/**
 * Custom settings for the period range filter
 */
export interface PeriodRangeFilterSettingsInterface {
  optionLabelKey: string;
  optionValueKey: string;
  initialRange: number;
}

export class PeriodRangeFilterSettings {
  optionLabelKey: string;
  optionValueKey: string;
  initialRange: number;

  constructor(args: PeriodRangeFilterSettingsInterface) {
    this.optionLabelKey = args.optionLabelKey;
    this.optionValueKey = args.optionValueKey;
    this.initialRange = args.initialRange;
  }
}

export interface PeriodRangeFilterUISettingsInterface extends DashboardFilterUISettingsInterface {
  enableInput?: boolean;
  valueDisplayMap?: Map<string, string>;
}

export class PeriodRangeFilterUISettings extends DashboardFilterUISettings {
  enableInput: boolean;
  valueDisplayMap?: Map<string, string>;

  constructor(args: PeriodRangeFilterUISettingsInterface) {
    super(args)
    this.enableInput = args.enableInput ?? false;
    this.valueDisplayMap = args.valueDisplayMap;
  }
}

/**
 * Filter
 */

// Helper class to manage previous state
class FilterState<V, PFV> {
  parentValue: PFV | null = null;
  value: V[] | null = null;
  selectedOptions: { first: DashboardFilterSelectOption, last: DashboardFilterSelectOption } | null = null;
}

export interface PeriodRangeFilterInterface<V, S extends PeriodRangeFilterSettings, US extends PeriodRangeFilterUISettings, O extends DashboardFilterSelectOption, PFV> extends DashboardFilterInterface<V[], PeriodRangeFilterUISettings, O, PFV> {
  options: O[];
  settings: S;
  uiSettings: US;
}

export class DashboardPeriodRangeFilterBase<V, PFV = any> extends DashboardFilter<V[], PeriodRangeFilterUISettings, DashboardFilterSelectOption, PFV> {
  readonly settings: PeriodRangeFilterSettings;
  private readonly _options: DashboardFilterSelectOption[];
  private _optionsFilteredByParentFilter: DashboardFilterSelectOption[] = [];
  private _valuesSubject: BehaviorSubject<V[]>;
  private _previousState: FilterState<V, PFV> = new FilterState();

  constructor(args: PeriodRangeFilterInterface<V, PeriodRangeFilterSettings, PeriodRangeFilterUISettings, DashboardFilterSelectOption, PFV>) {
    super(args);
    this.settings = args.settings;
    this._options = args.options;
    this._valuesSubject = new BehaviorSubject<V[]>(args.initialValue);
  }

  override get value(): V[] {
    return this._valuesSubject.value;
  }

  override set value(value: V[]) {
    this._valuesSubject.next(value);
  }

  override setValueNoChecks(value: V[]) {
    this._valuesSubject.next(value);
  }

  override get value$(): Observable<V[]> {
    return this._valuesSubject.pipe(
      debounceTime(350),
      distinctUntilChanged()
    );
  }

  isValueValid(value: any): boolean {
    return true;
  }

  private _options$!: Observable<DashboardFilterSelectOption[]>;

  get options$(): Observable<DashboardFilterSelectOption[]> {
    return this._options$;
  }

  private _getOptionsObservable(): Observable<DashboardFilterSelectOption[]> {
    if (!this.parentFilter || !this.parentFilterSettings) {
      this._optionsFilteredByParentFilter = this._options;
      return of(this._options);
    }

    return this.parentFilter.value$.pipe(
      map(parentValue => this.parentFilterSettings!.getFilteredChildOptions(parentValue, this._options)),
      tap(filteredOptions => {
        this._optionsFilteredByParentFilter = filteredOptions;
        this.onParentFilterChanged(filteredOptions);
      })
    );
  }

  onParentFilterChanged(filteredOptions: DashboardFilterSelectOption[]) {
    const parentValue = this.parentFilter?.value ?? null;

    // If current parent value is undefined/null, reset to default range
    if (!parentValue) {
      const defaultValue = this.calculateInitialValue(filteredOptions);
      this.setValue(defaultValue);
      this._previousState.parentValue = parentValue
      return;
    }

    // Do nothing when parentValue doesn't change
    if (this._previousState.parentValue === parentValue) {
      return;
    }

    // If previous parent value was undefined/null, treat as initial state
    const previousParentValue = this._previousState.parentValue ?? 'initial';
    const previousSelection = this._previousState.selectedOptions ?? this.getDefaultSelectedOptions();

    const transitionKey = this.createTransitionKey(previousParentValue as string, parentValue as string);
    const transitionColumn = this.parentValueTransitionKeyMap[transitionKey];
    const valueToSet: V[] = this.getSelectedRangeValues(filteredOptions, previousSelection, transitionColumn)
    this.setValue(valueToSet);

    this._previousState.parentValue = parentValue;
  }

  private parentValueTransitionKeyMap: Record<string, string> = {
    'quarter_to_year': 'year_id',
    'month_to_year': 'year_id',
    'year_to_quarter': 'year_id',
    'month_to_quarter': 'quarter_id',
    'year_to_month': 'year_id',
    'quarter_to_month': 'quarter_id',
  }

  private createTransitionKey(prevParentValue: string, currentParentValue: string) {
    return `${prevParentValue}_to_${currentParentValue}`;
  }

  private getSelectedRangeValues(filteredOptions: DashboardFilterSelectOption[], previousSelection: { first: DashboardFilterSelectOption; last: DashboardFilterSelectOption; }, bridgeColumn: string) {
    const startIndex = filteredOptions.findIndex(option => option[bridgeColumn] === previousSelection.first![bridgeColumn]);
    const endIndex = filteredOptions.map(option => option[bridgeColumn] === previousSelection.last![bridgeColumn]).lastIndexOf(true);

    if (startIndex === -1 || endIndex === -1) {
      return [];
    }

    const selectedOptions = filteredOptions.slice(startIndex, endIndex + 1);
    const values = selectedOptions.map(opt => opt[this.settings.optionValueKey]);
    return values;
  }

  private calculateInitialValue(options: DashboardFilterSelectOption[]): V[] {
    if (options.length === 0) {
      return [];
    }

    const start = Math.max(1, options.length - this.settings.initialRange);

    return options
      .slice(start)
      .map(opt => opt[this.settings.optionValueKey]);
  }

  private getDefaultSelectedOptions(): { first: DashboardFilterSelectOption, last: DashboardFilterSelectOption } {
    const firstDefault = this._optionsFilteredByParentFilter[0];
    const lastDefault = this._optionsFilteredByParentFilter[this._optionsFilteredByParentFilter.length - 1];
    return { first: firstDefault, last: lastDefault };
  }

  private getSelectedOptions(values: V[]): { first: DashboardFilterSelectOption, last: DashboardFilterSelectOption } | null {
    if (!values.length || !this._optionsFilteredByParentFilter.length) {
      return null;
    }

    const first = this._optionsFilteredByParentFilter.find(opt => opt.value === values[0]);
    const last = this._optionsFilteredByParentFilter.find(opt => opt.value === values[values.length - 1]);

    if (!first || !last) {
      console.warn('Could not find selected options in filtered options');
      return null;
    }

    return { first, last };
  }

  override setValue(val: V[]) {
    this._previousState.selectedOptions = this.getSelectedOptions(val)
    this._previousState.value = [...this.value];
    this._valuesSubject.next(val);
  }

  override initialize() {
    if (this.parentFilterSettings && !this.parentFilter || !this.parentFilterSettings && this.parentFilter) {
      throw Error(`${this.name}: Parent filter and its settings must be provided before initializing options`);
    }

    this._options$ = this._getOptionsObservable();

    // Set initial value
    firstValueFrom(this.options$).then(options => {
      const initialStart = options.length - this.settings.initialRange;
      const initialEnd = options.length;

      const selectedOptions = options.filter((_opt, idx) => idx >= initialStart && idx <= initialEnd)
      const initialValue = selectedOptions.map((opt) => opt[this.settings.optionValueKey]);

      this.setValue(initialValue)
    })
  }
}
